import { GiftItemStatus, IGiftItemResponse } from '@one-tree/library'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import React, {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { useOrganisation } from '../../../../../context/OrganisationProvider'
import { deleteQuestion, getOrganisationQuestions } from '../../../../../helpers/APIHelper'
import { getGiftItemQuestions } from '../../../../../helpers/APIItemHelper'
import {
  BulkQuestionReorder,
  IGiftItemPostBody,
} from '../../../../../types/GiftItemTypes'
import { PortalTicketQuestion } from '../../../../../types/Types'
import { priceUsingQuestion } from '../TicketPrices/pricesHelper'
import SortableRow from './SortableRow'
import TicketAddQuestions from './TicketAddQuestions'
import { SimpleTable } from '../SimpleTable'

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`
interface ITicketQuestionsProps {
  ticket: IGiftItemResponse
  changeTicket: (changes: IGiftItemPostBody) => void
  questionsKey: number
  setQuestionReorders: Dispatch<SetStateAction<BulkQuestionReorder[]>>
}
export default function TicketQuestions(
  props: ITicketQuestionsProps,
): ReactElement | null {
  const { ticket, changeTicket, questionsKey, setQuestionReorders } = props
  const { organisation } = useOrganisation()
  const [allQuestions, setAllQuestions] = useState<PortalTicketQuestion[]>()
  const [disableDelete, setDisableDelete] = useState<number[]>([])

  const sensors = useSensors(useSensor(PointerSensor))

  const activePricesCount = (ticket.prices ?? []).filter(
    ({ status }) => status !== GiftItemStatus.Archived,
  ).length
  const prevActivePricesCountRef = useRef(activePricesCount)

  const sortQuestions = (
    questions: PortalTicketQuestion[],
  ): PortalTicketQuestion[] => [...questions].sort((a, b) => {
      const aActive = a.used || priceUsingQuestion(a.id, ticket.prices)
      const bActive = b.used || priceUsingQuestion(b.id, ticket.prices)
      if (aActive && !bActive) return -1
      if (!aActive && bActive) return 1
      return a.order - b.order
    })

  const getQuestions = async (): Promise<void> => {
    if (!organisation) return

    if (ticket.id) {
      const res = await getGiftItemQuestions({
        format: organisation.format,
        itemId: ticket.id,
      })
      if (res) setAllQuestions(sortQuestions(res))
    } else {
      const res = await getOrganisationQuestions()
      if (res) setAllQuestions(sortQuestions(res))
    }
  }

  useEffect(() => {
    getQuestions()
  }, [questionsKey])

  const handleQuestionsUpdate = (orgQuestions: PortalTicketQuestion[]): void => {
    const currentQuestions = allQuestions ?? []
    const currentIds = new Set(currentQuestions.map(({ id }) => id))
    const newOrgQuestions = orgQuestions.filter(({ id }) => !currentIds.has(id))

    if (!newOrgQuestions.length) return

    const newIds = newOrgQuestions.map(({ id }) => id)
    const activePrices = (ticket.prices ?? []).filter(
      ({ status }) => status !== GiftItemStatus.Archived,
    )

    if (activePrices.length) {
      const updatedPrices = (ticket.prices ?? []).map((price) => {
        if (price.status === GiftItemStatus.Archived) return price
        return { ...price, enabledQuestionIds: [...(price.enabledQuestionIds ?? []), ...newIds] }
      })
      changeTicket({ prices: updatedPrices })

      const prependToPrice = (reorder: BulkQuestionReorder): BulkQuestionReorder => {
        if (!activePrices.find(({ id }) => id === reorder.itemId)) return reorder
        const existing = reorder.questions.map(({ id }) => id)
        const questions = [...newIds, ...existing].map((id, i) => ({ id, order: i + 1 }))
        return { ...reorder, questions }
      }
      setQuestionReorders((previous) => {
        const existingItemIds = new Set(previous.map(({ itemId }) => itemId))
        const created = activePrices
          .filter(({ id }) => id !== undefined && !existingItemIds.has(id))
          .map(({ id, enabledQuestionIds }) => {
            const merged = [...newIds, ...(enabledQuestionIds ?? [])]
            const questions = merged.map((qId, i) => ({ id: qId, order: i + 1 }))
            return { itemId: id ?? 0, questions }
          })
        return [...previous.map(prependToPrice), ...created]
      })
    } else {
      changeTicket({ enabledQuestionIds: [...(ticket.enabledQuestionIds ?? []), ...newIds] })

      const activeIds = currentQuestions
        .filter(({ id, used }) => used || priceUsingQuestion(id, ticket.prices))
        .map(({ id }) => id)

      const prependToTicket = (reorder: BulkQuestionReorder): BulkQuestionReorder => {
        if (reorder.itemId !== ticket.id) return reorder
        const existing = reorder.questions.map(({ id }) => id)
        const questions = [...newIds, ...existing].map((id, i) => ({ id, order: i + 1 }))
        return { ...reorder, questions }
      }
      setQuestionReorders((previous) => {
        if (!ticket.id) return previous
        if (previous.some(({ itemId }) => itemId === ticket.id)) {
          return previous.map(prependToTicket)
        }
        const questions = [...newIds, ...activeIds].map((id, i) => ({ id, order: i + 1 }))
        return [...previous, { itemId: ticket.id, questions }]
      })
    }

    const newMarkedUsed = newOrgQuestions.map((q) => ({ ...q, used: true, order: 0 }))
    setAllQuestions(sortQuestions([...newMarkedUsed, ...currentQuestions]))
  }

  // Stage a reorder for an item using the current display order of its enabled questions
  const syncReorderForItem = (itemId: number, enabledIds: number[]): void => {
    if (!allQuestions) return
    const enabled = new Set(enabledIds)
    const questions = allQuestions
      .filter(({ id }) => enabled.has(id))
      .map(({ id }, i) => ({ id, order: i + 1 }))
    setQuestionReorders((previous) => [
      ...previous.filter((reorder) => reorder.itemId !== itemId),
      { itemId, questions },
    ])
  }

  const handleToggleQuestion = (enabledQuestionIds: number[]): void => {
    changeTicket({ enabledQuestionIds })
    if (ticket.id) syncReorderForItem(ticket.id, enabledQuestionIds)
  }

  const handlePriceToggle = (
    priceId: number,
    enabledQuestionIds: number[],
  ): void => {
    syncReorderForItem(priceId, enabledQuestionIds)
  }

  useEffect(() => {
    const prev = prevActivePricesCountRef.current
    prevActivePricesCountRef.current = activePricesCount
    if (
      prev === 0
      && activePricesCount > 0
      && ticket.enabledQuestionIds?.length
    ) {
      handleToggleQuestion([])
    }
  }, [activePricesCount])

  const handleDeleteQuestion = async (questionId: number): Promise<void> => {
    if (organisation) {
      const res = await deleteQuestion({ orgId: organisation.id, questionId })
      if (res) await getQuestions()
    }
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event
    if (!over || active.id === over.id || !allQuestions || !ticket.id) return

    const oldIndex = allQuestions.findIndex(({ id }) => id === active.id)
    const newIndex = allQuestions.findIndex(({ id }) => id === over.id)
    const draggedId = allQuestions[oldIndex].id

    const newQuestions = arrayMove(allQuestions, oldIndex, newIndex)
    setAllQuestions(newQuestions)

    const withNewOrders = newQuestions.map((q, i) => ({ ...q, order: i + 1 }))

    const toEntry = (itemId: number, enabledIds: Set<number>): BulkQuestionReorder => ({
      itemId,
      questions: withNewOrders
        .filter(({ id }) => enabledIds.has(id))
        .map(({ id, order }) => ({ id, order })),
    })

    // Reorder on each active price using the question, or the parent ticket if none do
    const activePrices = (ticket.prices ?? []).filter(
      ({ status }) => status !== GiftItemStatus.Archived,
    )
    const pricesWithDragged = activePrices.filter(
      ({ enabledQuestionIds }) => enabledQuestionIds?.includes(draggedId),
    )

    const reorders = pricesWithDragged.length
      ? pricesWithDragged.map(({ id, enabledQuestionIds }) => {
          const enabled = new Set(enabledQuestionIds ?? [])
          return toEntry(id, enabled)
        })
      : [
          toEntry(
            ticket.id,
            new Set(
              withNewOrders
                .filter(({ id, used }) => used || priceUsingQuestion(id, ticket.prices))
                .map(({ id }) => id),
            ),
          ),
        ]

    // Defer the reorders, saved on Save in EditGiftItem
    const reorderItemIds = reorders.map(({ itemId }) => itemId)
    setQuestionReorders((previous) => [
      ...previous.filter(({ itemId }) => !reorderItemIds.includes(itemId)),
      ...reorders,
    ])
  }

  return (
    <Styles>
      <h3>Ticket questions</h3>
      <TicketAddQuestions onQuestionAdded={handleQuestionsUpdate} />
      {allQuestions && allQuestions.length ? (
        <SimpleTable>
          <tbody>
            <tr>
              <th />
              <th>Enable</th>
              <th>Title</th>
              <th>Type</th>
              <th>Options</th>
              <th>Delete</th>
            </tr>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={allQuestions.map(({ id }) => id)}
                strategy={verticalListSortingStrategy}
              >
                {allQuestions.map((question) => (
                  <SortableRow
                    key={question.id}
                    question={question}
                    ticket={ticket}
                    changeTicket={changeTicket}
                    onToggleQuestion={handleToggleQuestion}
                    onPriceToggle={handlePriceToggle}
                    disableDelete={disableDelete}
                    setDisableDelete={setDisableDelete}
                    onDelete={handleDeleteQuestion}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </tbody>
        </SimpleTable>
      ) : null}
    </Styles>
  )
}
